import { unsafeCSS } from "lit";
import { Selection, Plugin, PluginKey } from "prosemirror-state";
import { Node } from "prosemirror-model";
import { Decoration, DecorationSet } from "prosemirror-view";
import { BaseExtension } from "@/wc/text-editor/lib/core/base-extension";

import { SlChangeEvent } from "@shoelace-style/shoelace";
import "@shoelace-style/shoelace/dist/components/copy-button/copy-button.js";
import "@shoelace-style/shoelace/dist/components/select/select.js";
import "@shoelace-style/shoelace/dist/components/option/option.js";

import style from "./style.scss?inline";
import highlightStyle from "highlight.js/styles/github.css?inline";
import { common, createLowlight } from "lowlight";
import { createHighlightPlugin } from "prosemirror-highlight";
import { createParser } from "prosemirror-highlight/lowlight";

export class CodeLanguagesExtension extends BaseExtension {
    private handleChangeLanguage = (
        node: Node,
        index: number,
        event: SlChangeEvent
    ) => {
        if (!this.view) return;

        // Find the position of the code block node using its index
        let foundPos: number | undefined;
        let currentIndex = -1;

        this.view.state.doc.descendants((node_, pos) => {
            if (node_.type.name === "code_block") {
                currentIndex++;
                if (currentIndex === index) {
                    foundPos = pos;
                    return false; // Stop traversing once found
                }
            }
        });

        if (foundPos === undefined) {
            console.warn(`Could not find code block with index: ${index}.`);
            return;
        }
        const resolvedPos = this.view.state.doc.resolve(foundPos);

        const transaction = this.view.state.tr
            .setSelection(Selection.near(resolvedPos))
            .setNodeMarkup(foundPos, undefined, {
                ...node.attrs,
                params: (event.target as HTMLInputElement)?.value,
            });
        this.view.dispatch(transaction);
    };

    private createCodeActionsDom = (
        node: Node,
        index: number,
        languages: string[]
    ) => {
        // Create container for code block codeActions
        const codeActionsContainer = document.createElement("div");
        codeActionsContainer.classList.add("code-block-actions");

        const select = document.createElement("sl-select");
        select.classList.add("code-language-selector");
        select.size = "small";
        select.disabled = !this.view?.editable;
        select.value = node.attrs.params || "plaintext";

        languages.forEach((lang) => {
            const option = document.createElement("sl-option");
            option.value = lang;
            option.textContent = lang;
            select.appendChild(option);
        });

        // Bind the ID immediately to prevent closure issues
        const boundHandler = this.handleChangeLanguage.bind(this, node, index);
        select.addEventListener("sl-change", boundHandler);

        // Add copy button
        const copyButton = document.createElement("sl-copy-button");
        copyButton.classList.add("code-copy-button");
        copyButton.value = node.textContent;

        // Add elements to container
        codeActionsContainer.appendChild(select);
        codeActionsContainer.appendChild(copyButton);

        return codeActionsContainer;
    };

    plugins = () => {
        const lowlight = createLowlight(common);
        const parser = createParser(lowlight);
        const lowlightPlugin = createHighlightPlugin({
            parser: (opts) => {
                if (opts.language === "mermaid") {
                    return [];
                }
                return parser(opts);
            },
            languageExtractor: (node) => node.attrs.params,
        });

        const languages = [...lowlight.listLanguages(), "mermaid"].toSorted();

        const codeActionsKey = new PluginKey();
        const codeActionsPlugin = new Plugin({
            key: codeActionsKey,
            props: {
                decorations: (state) => {
                    const decorations: Decoration[] = [];
                    state.doc.descendants((node, pos) => {
                        if (node.type.name === "code_block") {
                            const currentIndex = decorations.length;
                            const codeActionsWidget = Decoration.widget(
                                pos + 1,
                                () =>
                                    this.createCodeActionsDom(
                                        node,
                                        currentIndex,
                                        languages
                                    ),
                                {
                                    key: `code-actions-${currentIndex}-${this.view?.editable}`,
                                    ignoreSelection: true,
                                    side: -1,
                                }
                            );
                            decorations.push(codeActionsWidget);
                        }
                    });
                    return DecorationSet.create(state.doc, decorations);
                },
            },
        });

        return [codeActionsPlugin, lowlightPlugin];
    };

    css = [unsafeCSS(highlightStyle), unsafeCSS(style)];
}
